import React, { useState, useEffect } from 'react';
import { meetingsAPI, aiAPI } from '../../../services/api';
import { Button, Card, Modal, Paginator } from '../../../components/ui';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

const PLATFORMS = ['Zoom', 'Google Meet', 'Microsoft Teams', 'In-Person', 'Phone'];

export default function MeetingsTab({ leadId }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', platform: 'Zoom', scheduled_at: '', duration: 30,
    zoom_link: '', attendees: '', notes: ''
  });
  const [aiPrep, setAiPrep] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [discussModal, setDiscussModal] = useState(null);
  const [discussForm, setDiscussForm] = useState({ outcome: 'Pending', notes: '' });
  const [discussSaving, setDiscussSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  // Clamp page to valid range when meetings are added/removed
  useEffect(() => {
    const tp = Math.ceil(meetings.length / PAGE_SIZE);
    if (tp > 0 && page > tp) setPage(tp);
  }, [meetings.length]);

  const load = async () => {
    try {
      const res = await meetingsAPI.getByLead(leadId);
      setMeetings(res.data?.data?.meetings || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [leadId]);

  const create = async () => {
    if (!form.title || !form.scheduled_at) { toast.error('Title and date required'); return; }
    const year = new Date(form.scheduled_at).getFullYear();
    if (year < 2000 || year > 2099) { toast.error('Please enter a valid year (2000–2099)'); return; }
    setSaving(true);
    try {
      await meetingsAPI.create({ ...form, lead_id: leadId });
      toast.success('Meeting logged');
      setModal(false);
      setForm({ title: '', platform: 'Zoom', scheduled_at: '', duration: 30, zoom_link: '', attendees: '', notes: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to log meeting');
    } finally { setSaving(false); }
  };

  const isPast = (m) => new Date(m.scheduled_at) < new Date();

  const deleteMeeting = async (m) => {
    if (!window.confirm(`Delete meeting "${m.title || 'Meeting'}"?`)) return;
    setDeletingId(m._id);
    try {
      await meetingsAPI.delete(m._id);
      setMeetings(ms => ms.filter(x => x._id !== m._id));
      toast.success('Meeting deleted');
    } catch {
      toast.error('Failed to delete');
    } finally { setDeletingId(null); }
  };

  const MEETING_OUTCOMES = ['Demo Given', 'MoU Discussed', 'Pricing Discussed', 'Not Interested', 'Follow-up Scheduled', 'Pending'];

  const openDiscuss = (m) => {
    setDiscussModal(m);
    setDiscussForm({
      outcome:                m.outcome || 'Pending',
      sentiment:              m.sentiment || '',
      interest_level:         m.interest_level || '',
      key_points:             m.key_points || '',
      objections:             m.objections || '',
      next_steps:             m.next_steps || '',
      decision_maker_present: m.decision_maker_present || false,
      notes:                  m.notes || '',
    });
  };

  const saveDiscussion = async () => {
    setDiscussSaving(true);
    try {
      const payload = {
        outcome:                discussForm.outcome,
        sentiment:              discussForm.sentiment || null,
        interest_level:         discussForm.interest_level || null,
        key_points:             discussForm.key_points || null,
        objections:             discussForm.objections || null,
        next_steps:             discussForm.next_steps || null,
        decision_maker_present: discussForm.decision_maker_present,
        notes:                  discussForm.notes || null,
      };
      const res = await meetingsAPI.update(discussModal._id, payload);
      setMeetings(ms => ms.map(m => m._id === discussModal._id ? (res.data?.data || { ...m, ...discussForm }) : m));
      toast.success('Meeting notes saved');
      setDiscussModal(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save notes');
    } finally { setDiscussSaving(false); }
  };

  const getAiPrep = async () => {
    setAiLoading(true);
    setAiPrep(null);
    try {
      const res = await aiAPI.tabAssist({ lead_id: leadId, tab: 'meeting' });
      setAiPrep(res.data?.data);
    } catch {
      toast.error('AI prep failed');
    } finally { setAiLoading(false); }
  };

  const applyAiPrep = () => {
    if (!aiPrep) return;
    setForm(f => ({
      ...f,
      platform: aiPrep.suggested_meeting_type || f.platform,
      notes: [
        aiPrep.desired_outcome ? `Goal: ${aiPrep.desired_outcome}` : '',
        aiPrep.agenda?.length ? `Agenda:\n${aiPrep.agenda.map((a, i) => `${i + 1}. ${a}`).join('\n')}` : '',
        aiPrep.talking_points?.length ? `Talking Points:\n${aiPrep.talking_points.map(p => `• ${p}`).join('\n')}` : ''
      ].filter(Boolean).join('\n\n')
    }));
    setAiPrep(null);
    setModal(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>Meetings / Zoom Logs</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" variant="ghost" onClick={getAiPrep} disabled={aiLoading}>
            {aiLoading ? '⏳ Thinking…' : '✨ AI Prep'}
          </Button>
          <Button size="sm" variant="primary" onClick={() => setModal(true)}>+ Log Meeting</Button>
        </div>
      </div>

      {/* AI Meeting Prep Panel */}
      {aiPrep && (
        <div style={{ background: '#f0f7ff', border: '1.5px solid #93c5fd', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>✨ AI Meeting Prep</span>
            <button onClick={() => setAiPrep(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}>✕</button>
          </div>
          <p style={{ fontSize: 12, color: '#475569', marginBottom: 10, fontStyle: 'italic' }}>{aiPrep.reasoning}</p>
          {aiPrep.desired_outcome && (
            <div style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 2 }}>MEETING GOAL</p>
              <p style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>{aiPrep.desired_outcome}</p>
            </div>
          )}
          {aiPrep.agenda?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>AGENDA</p>
              {aiPrep.agenda.map((a, i) => <p key={i} style={{ fontSize: 12, color: '#334155', marginBottom: 2 }}>{i + 1}. {a}</p>)}
            </div>
          )}
          {aiPrep.talking_points?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>TALKING POINTS</p>
              {aiPrep.talking_points.map((p, i) => <p key={i} style={{ fontSize: 12, color: '#334155', marginBottom: 2 }}>• {p}</p>)}
            </div>
          )}
          {aiPrep.objections_to_prepare_for?.length > 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>PREPARE FOR THESE OBJECTIONS</p>
              {aiPrep.objections_to_prepare_for.map((o, i) => <p key={i} style={{ fontSize: 12, color: '#78350f', marginBottom: 2 }}>⚠ {o}</p>)}
            </div>
          )}
          <Button size="sm" variant="primary" onClick={applyAiPrep}>Use as Meeting Notes →</Button>
        </div>
      )}

      {loading ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</p> : meetings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
          <p style={{ fontSize: 28, marginBottom: 8 }}>🗓️</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>No meetings logged yet</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Click <strong>+ Log Meeting</strong> to record a Zoom, Google Meet, or in-person meeting.</p>
          <p style={{ fontSize: 12, marginTop: 4, color: '#94a3b8' }}>Each meeting will have a <strong>📝 Log Discussion</strong> button to capture what was discussed.</p>
        </div>
      ) : meetings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(m => (
        <Card key={m._id} style={{ marginBottom: 10, padding: '14px 16px', border: m.outcome && m.outcome !== 'Pending' ? '1px solid #e2e8f0' : isPast(m) ? '1.5px solid #fcd34d' : '1.5px solid #bfdbfe' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{m.title || 'Meeting'}</span>
                {m.platform && <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>{m.platform}</span>}
                {m.outcome && m.outcome !== 'Pending' && (
                  <span style={{ fontSize: 11, background: '#dcfce7', color: '#16a34a', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>✓ {m.outcome}</span>
                )}
                {isPast(m) && (!m.outcome || m.outcome === 'Pending') && (
                  <span style={{ fontSize: 11, background: '#fef3c7', color: '#d97706', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>⚠ Notes pending</span>
                )}
              </div>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: m.attendees?.length || m.zoom_link || m.notes ? 6 : 0 }}>
                📅 {new Date(m.scheduled_at).toLocaleString('en-IN')}{m.duration ? ` · ⏱ ${m.duration} min` : ''}
              </p>
              {m.zoom_link && (
                <a href={m.zoom_link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', fontSize: 12, color: '#2563eb', textDecoration: 'none', marginBottom: 4 }}>🔗 Join Link</a>
              )}
              {m.attendees?.length > 0 && (
                <p style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>👥 {m.attendees.map(a => a.name).filter(Boolean).join(', ')}</p>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4, marginBottom: 4 }}>
                {m.sentiment && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: m.sentiment.includes('Positive') ? '#f0fdf4' : m.sentiment === 'Neutral' ? '#fefce8' : '#fef2f2',
                    color:      m.sentiment.includes('Positive') ? '#15803d' : m.sentiment === 'Neutral' ? '#a16207' : '#dc2626',
                    border:     `1px solid ${m.sentiment.includes('Positive') ? '#bbf7d0' : m.sentiment === 'Neutral' ? '#fde68a' : '#fecaca'}` }}>
                    {m.sentiment === 'Very Positive' ? '😄' : m.sentiment === 'Positive' ? '🙂' : m.sentiment === 'Neutral' ? '😐' : m.sentiment === 'Negative' ? '😟' : '😠'} {m.sentiment}
                  </span>
                )}
                {m.interest_level && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: m.interest_level === 'Hot' ? '#fff7ed' : m.interest_level === 'Warm' ? '#fefce8' : '#f8fafc',
                    color:      m.interest_level === 'Hot' ? '#c2410c' : m.interest_level === 'Warm' ? '#a16207' : '#64748b',
                    border:     `1px solid ${m.interest_level === 'Hot' ? '#fed7aa' : m.interest_level === 'Warm' ? '#fde68a' : '#e2e8f0'}` }}>
                    {m.interest_level === 'Hot' ? '🔥' : m.interest_level === 'Warm' ? '🌤️' : m.interest_level === 'Not Interested' ? '❌' : '🧊'} {m.interest_level}
                  </span>
                )}
                {m.decision_maker_present && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#ede9fe', color: '#7c3aed', border: '1px solid #ddd6fe' }}>
                    👔 Decision Maker Present
                  </span>
                )}
              </div>
              {m.key_points && (
                <div style={{ background: '#f0f9ff', borderLeft: '3px solid #38bdf8', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 2 }}>KEY POINTS</p>
                  <p style={{ fontSize: 12, color: '#0c4a6e', whiteSpace: 'pre-wrap' }}>{m.key_points}</p>
                </div>
              )}
              {m.objections && (
                <div style={{ background: '#fff7ed', borderLeft: '3px solid #fb923c', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#c2410c', marginBottom: 2 }}>OBJECTIONS / CONCERNS</p>
                  <p style={{ fontSize: 12, color: '#7c2d12', whiteSpace: 'pre-wrap' }}>{m.objections}</p>
                </div>
              )}
              {m.next_steps && (
                <div style={{ background: '#f0fdf4', borderLeft: '3px solid #4ade80', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 2 }}>NEXT STEPS</p>
                  <p style={{ fontSize: 12, color: '#14532d', whiteSpace: 'pre-wrap' }}>{m.next_steps}</p>
                </div>
              )}
              {m.notes && (
                <div style={{ background: '#f8fafc', borderLeft: '3px solid #cbd5e1', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginTop: 2 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 2 }}>NOTES</p>
                  <p style={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>{m.notes}</p>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
              <Button size="sm" variant={m.notes || m.key_points || m.sentiment ? 'ghost' : 'primary'} onClick={() => openDiscuss(m)}>
                {m.notes || m.key_points || m.sentiment ? '✏️ Edit Notes' : '📝 Log Discussion'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteMeeting(m)} disabled={deletingId === m._id}
                style={{ fontSize: 11, color: '#ef4444', borderColor: '#fecaca' }}>
                {deletingId === m._id ? '…' : 'Delete'}
              </Button>
            </div>
          </div>
        </Card>
      ))}

      <Paginator page={page} totalPages={Math.ceil(meetings.length / PAGE_SIZE)} onPageChange={setPage} />

      <Modal open={modal} onClose={() => setModal(false)} title="Log Meeting">
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inp} placeholder="Meeting title" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Platform</label>
              <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} style={sel}>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Duration (min)</label>
              <input type="number" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} style={inp} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Date & Time *</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} min="2000-01-01T00:00" max="2099-12-31T23:59" style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Meeting Link</label>
            <input value={form.zoom_link} onChange={e => setForm(f => ({ ...f, zoom_link: e.target.value }))} style={inp} placeholder="https://zoom.us/j/..." />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Attendees</label>
            <input value={form.attendees} onChange={e => setForm(f => ({ ...f, attendees: e.target.value }))} style={inp} placeholder="Names / emails, comma-separated" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, minHeight: 70, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={create} disabled={saving}>{saving ? 'Saving…' : 'Log Meeting'}</Button>
          </div>
        </div>
      </Modal>

      {/* Log Discussion Modal */}
      <Modal open={!!discussModal} onClose={() => setDiscussModal(null)}
        title={discussModal?.notes ? `Edit Discussion — ${discussModal?.title || 'Meeting'}` : `Log Discussion — ${discussModal?.title || 'Meeting'}`}>
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
          📅 {discussModal ? new Date(discussModal.scheduled_at).toLocaleString('en-IN') : ''}
          {discussModal?.platform && <> · {discussModal.platform}</>}
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          {/* Row 1: Outcome + Sentiment */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Outcome</label>
              <select value={discussForm.outcome} onChange={e => setDiscussForm(f => ({ ...f, outcome: e.target.value }))} style={sel}>
                {MEETING_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Sentiment / Mood</label>
              <select value={discussForm.sentiment} onChange={e => setDiscussForm(f => ({ ...f, sentiment: e.target.value }))} style={sel}>
                <option value="">Select sentiment…</option>
                {['Very Positive', 'Positive', 'Neutral', 'Negative', 'Very Negative'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Interest Level + Decision Maker */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Interest Level</label>
              <select value={discussForm.interest_level} onChange={e => setDiscussForm(f => ({ ...f, interest_level: e.target.value }))} style={sel}>
                <option value="">Select level…</option>
                {['Hot', 'Warm', 'Cold', 'Not Interested'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569', padding: '8px 0' }}>
                <input type="checkbox" checked={discussForm.decision_maker_present}
                  onChange={e => setDiscussForm(f => ({ ...f, decision_maker_present: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                Decision Maker was present
              </label>
            </div>
          </div>

          {/* Row 3: Key Points */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Key Points Discussed
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— Topics covered, demos given, decisions made</span>
            </label>
            <textarea value={discussForm.key_points} onChange={e => setDiscussForm(f => ({ ...f, key_points: e.target.value }))}
              style={{ ...inp, minHeight: 72, resize: 'vertical' }}
              placeholder="e.g. Gave product demo. Discussed AIAAI certification value. Covered batch schedules and fee structure. They liked online mode." />
          </div>

          {/* Row 4: Objections */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Objections / Concerns Raised
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— Hesitations, blockers, competitor mentions</span>
            </label>
            <textarea value={discussForm.objections} onChange={e => setDiscussForm(f => ({ ...f, objections: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }}
              placeholder="e.g. Wants 15% discount for 60+ students. HOD not sure about weekend batches. Trustee approval pending." />
          </div>

          {/* Row 5: Next Steps */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Next Steps Agreed
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— Action items, follow-up commitments</span>
            </label>
            <textarea value={discussForm.next_steps} onChange={e => setDiscussForm(f => ({ ...f, next_steps: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }}
              placeholder="e.g. Send MoU draft by Friday. They will get Trustee sign-off by next week. Schedule follow-up call in 5 days." />
          </div>

          {/* Row 6: Additional Notes */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Additional Notes
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— Tone, context, background, anything notable</span>
            </label>
            <textarea value={discussForm.notes} onChange={e => setDiscussForm(f => ({ ...f, notes: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }}
              placeholder="e.g. Very warm meeting. Principal was enthusiastic. They mentioned Simplilearn approached them last month but were not satisfied." />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setDiscussModal(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveDiscussion} disabled={discussSaving}>{discussSaving ? 'Saving…' : 'Save Notes'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
